/**
 * PeriodPicker — the scorecard / Reps page period: This month (default),
 * Last month, This quarter, Last quarter, Custom range (from–to).
 *
 * State lives in the URL (?period=&from=&to=) so a drill-down link, a refresh
 * or a shared link keeps the same period. The backend resolves the actual
 * window in IST (backend/src/modules/analytics/scorecard-period.ts); this file
 * only builds the query and catches an inverted / half-filled custom range.
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export const PERIOD_OPTIONS = [
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'this_quarter', label: 'This quarter' },
  { id: 'last_quarter', label: 'Last quarter' },
  { id: 'custom', label: 'Custom range' },
] as const;

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The API query for the period in `params`: '' = default (this month).
 * Returns { error } for a custom range the server would refuse.
 */
export function periodQuery(params: URLSearchParams): { query: string } | { error: string } {
  const period = params.get('period') ?? '';
  if (!period || period === 'this_month') return { query: '' };
  if (period !== 'custom') return { query: new URLSearchParams({ period }).toString() };
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  if (!DAY.test(from) || !DAY.test(to)) return { error: 'Pick both a From and a To date' };
  if (from > to) return { error: 'From date must be on or before the To date' };
  return { query: new URLSearchParams({ period, from, to }).toString() };
}

/** URL-backed period state + the API query string ('' when default). */
export function usePeriodParams() {
  const [params, setParams] = useSearchParams();
  const period = params.get('period') ?? 'this_month';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const q = periodQuery(params);
  const set = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === '') next.delete(k);
        else next.set(k, v);
      }
      if (next.get('period') !== 'custom') {
        next.delete('from');
        next.delete('to');
      }
      if (next.get('period') === 'this_month') next.delete('period');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  return {
    period,
    from,
    to,
    query: 'query' in q ? q.query : null,
    error: 'error' in q ? q.error : null,
    set,
  };
}

/** Append the period query to a path that may already have a query string. */
export function withPeriod(path: string, query: string | null): string {
  if (!query) return path;
  return path + (path.includes('?') ? '&' : '?') + query;
}

export function PeriodPicker({
  state,
  rangeLabel,
}: {
  state: ReturnType<typeof usePeriodParams>;
  rangeLabel?: string | null;
}) {
  const today = new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10); // IST date
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="period-picker">
      <label className="flex flex-col text-[11px] uppercase tracking-wide text-slate-500">
        Period
        <select
          aria-label="Period"
          className="mt-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm normal-case tracking-normal text-slate-800"
          value={state.period}
          onChange={(e) => state.set({ period: e.target.value })}
        >
          {PERIOD_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      {state.period === 'custom' && (
        <>
          <label className="flex flex-col text-[11px] uppercase tracking-wide text-slate-500">
            From
            <input
              type="date"
              aria-label="From date"
              max={today}
              className="mt-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm text-slate-800"
              value={state.from}
              onChange={(e) => state.set({ from: e.target.value })}
            />
          </label>
          <label className="flex flex-col text-[11px] uppercase tracking-wide text-slate-500">
            To
            <input
              type="date"
              aria-label="To date"
              max={today}
              className="mt-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm text-slate-800"
              value={state.to}
              onChange={(e) => state.set({ to: e.target.value })}
            />
          </label>
        </>
      )}
      {rangeLabel && !state.error && (
        <span className="pb-1.5 text-sm font-medium text-slate-700">{rangeLabel}</span>
      )}
      {state.error && (
        <span role="alert" className="pb-1.5 text-sm text-[#8A2A3A]">
          {state.error}
        </span>
      )}
    </div>
  );
}
